import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import { UserService } from '../../data/service/user.service';

/** The subset of Account/UserGetByIdServer the navigation surfaces actually use. */
export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: number;
  organizationId: string;
}

/**
 * SD-1218: one cached read of the current user, replacing 31 uncached calls.
 *
 * `UserService.getUserGetByIdServer()` is called from 27 files -- the sidebar, the header,
 * both guards, every settings page, paperwork, emails, payable, automate -- and none of them
 * cache, so a single page load fires the same request many times over for the same role byte.
 *
 * Two things about this endpoint are worth knowing before touching this file:
 *
 *  1. It returns **HTTP 200 on failure**, with `{ status: 1, Message: "User Not Exist" }`
 *     (AccountController.cs:935). `shareReplay` would latch that forever and the user would
 *     be stuck with no menu until a full reload. That is why a non-zero status maps to null
 *     and why `refresh()` is the only thing that re-fetches.
 *
 *  2. Because nothing cached before, every existing caller got fresh data for free. Once
 *     this service is the source, "call it again to get fresh data" no longer works -- any
 *     code that changes a user's role or organisation must call `refresh()`.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {

  private _refresh$ = new BehaviorSubject<number>(0);

  readonly user$: Observable<SessionUser | null> = this._refresh$.pipe(
    switchMap(() => this.userService.getUserGetByIdServer().pipe(
      map(res => this.toSessionUser(res)),
      catchError(() => of(null))
    )),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  readonly role$: Observable<number | null> = this.user$.pipe(
    map(user => user ? user.role : null),
    distinctUntilChanged()
  );

  /** Convenience for templates that still compare role as a string. */
  readonly roleId$: Observable<string | null> = this.role$.pipe(
    map(role => role === null || role === undefined ? null : String(role))
  );

  readonly isSuperAdmin$: Observable<boolean> = this.role$.pipe(
    map(role => role === 1),
    distinctUntilChanged()
  );

  constructor(private userService: UserService) { }

  /** Re-fetches. Call after anything that changes the user's role or organisation. */
  refresh(): void {
    this._refresh$.next(this._refresh$.value + 1);
  }

  private toSessionUser(res: any): SessionUser | null {
    // status 0 is success in this envelope; 1 is the failure case that still arrives as a 200.
    if (!res || res.status !== 0 || !res.data) {
      return null;
    }

    const data = res.data;
    const role = data.role;

    if (role === undefined || role === null) {
      return null;
    }

    return {
      id: data.id,
      email: data.email,
      name: data.name,
      role: Number(role),
      organizationId: data.organizationId
    };
  }
}
